import { useState, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuthStore } from '@/stores/authStore'
import { Button } from '@/components/ui/Button'
import { ShieldCheck, Loader2, Moon, Sun } from 'lucide-react'
import api from '@/lib/api'

export default function Setup2FAPage() {
  const navigate = useNavigate()
  const { token, refreshToken, login, logout } = useAuthStore()

  const [theme, setTheme] = useState<'light' | 'dark'>('light')
  const [qrCode, setQrCode] = useState<string | null>(null)
  const [secret, setSecret] = useState<string | null>(null)
  const [code, setCode] = useState(['', '', '', '', '', ''])
  const [isLoading, setIsLoading] = useState(true)
  const [isVerifying, setIsVerifying] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const inputRefs = useRef<(HTMLInputElement | null)[]>([])

  useEffect(() => {
    const stored = localStorage.getItem('nai-theme')
    if (stored === 'dark' || stored === 'light') {
      setTheme(stored)
      document.documentElement.classList.toggle('dark', stored === 'dark')
    } else if (window.matchMedia('(prefers-color-scheme: dark)').matches) {
      setTheme('dark')
      document.documentElement.classList.add('dark')
    }
  }, [])

  const toggleTheme = () => {
    const newTheme = theme === 'dark' ? 'light' : 'dark'
    setTheme(newTheme)
    localStorage.setItem('nai-theme', newTheme)
    document.documentElement.classList.toggle('dark', newTheme === 'dark')
  }

  useEffect(() => {
    if (!token) {
      navigate('/login', { replace: true })
      return
    }
    fetchTotpSetup()
  }, [])

  async function fetchTotpSetup() {
    setIsLoading(true)
    try {
      const { data } = await api.post('/v1/users/me/totp-setup', null, {
        headers: { Authorization: `Bearer ${token}` },
      })
      setQrCode(data.qr_code)
      setSecret(data.secret)
    } catch {
      setError('Erro ao gerar QR code. Tente novamente.')
    } finally {
      setIsLoading(false)
    }
  }

  function handleInput(index: number, value: string) {
    if (!/^\d*$/.test(value)) return
    const newCode = [...code]
    newCode[index] = value.slice(-1)
    setCode(newCode)
    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus()
    }
    const fullCode = newCode.join('')
    if (fullCode.length === 6) {
      handleVerify(fullCode)
    }
  }

  function handleKeyDown(index: number, e: React.KeyboardEvent) {
    if (e.key === 'Backspace' && !code[index] && index > 0) {
      inputRefs.current[index - 1]?.focus()
    }
  }

  function handlePaste(e: React.ClipboardEvent) {
    e.preventDefault()
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6)
    if (pasted.length === 6) {
      setCode(pasted.split(''))
      inputRefs.current[5]?.focus()
      handleVerify(pasted)
    }
  }

  async function handleVerify(verifyCode?: string) {
    const codeStr = verifyCode || code.join('')
    if (codeStr.length !== 6) {
      setError('Digite o codigo de 6 digitos')
      return
    }
    setIsVerifying(true)
    setError(null)
    try {
      await api.post('/v1/users/me/totp-verify', { code: codeStr }, {
        headers: { Authorization: `Bearer ${token}` },
      })
      const { data: userData } = await api.get('/v1/auth/me', {
        headers: { Authorization: `Bearer ${token}` },
      })
      const updatedUser = {
        id: userData.id,
        email: userData.email,
        first_name: userData.first_name,
        last_name: userData.last_name,
        full_name: `${userData.first_name} ${userData.last_name}`,
        role_id: userData.role?.id || '',
        role: userData.role || null,
        team_id: null,
        team: null,
        is_active: userData.is_active,
        attributes: userData.attributes || {},
        totp_enabled: true,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      }
      login(updatedUser, token!, refreshToken!)
      navigate('/', { replace: true })
    } catch {
      setError('Codigo invalido. Tente novamente.')
      setCode(['', '', '', '', '', ''])
      setTimeout(() => inputRefs.current[0]?.focus(), 100)
    } finally {
      setIsVerifying(false)
    }
  }

  function handleCancel() {
    logout()
    navigate('/login', { replace: true })
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-950 px-4 py-8">
      {/* Theme Toggle */}
      <div className="fixed top-6 right-6 z-10">
        <button
          onClick={toggleTheme}
          className="w-10 h-10 rounded-full flex items-center justify-center bg-white dark:bg-gray-800 shadow-md hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
        >
          {theme === 'dark' ? <Sun className="h-5 w-5 text-yellow-500" /> : <Moon className="h-5 w-5 text-gray-600" />}
        </button>
      </div>

      <div className="w-full max-w-3xl">
        {/* Logo */}
        <div className="flex justify-center mb-8">
          <img src="/Logo-nai-growth.webp" alt="Nai Growth" className="h-14 object-contain dark:hidden" />
          <img src="/Logo-nai-growth-branco.webp" alt="Nai Growth" className="h-14 object-contain hidden dark:block" />
        </div>

        {/* Card */}
        <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-xl border border-gray-200 dark:border-gray-800">
          <div className="px-10 py-10 md:px-14 md:py-12">
            {/* Header */}
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-3">
              Autenticação em duas etapas
            </h1>
            <p className="text-gray-500 dark:text-gray-400 text-sm leading-relaxed mb-6 max-w-xl">
              Proteja sua conta com uma etapa adicional de verificacao. Use um aplicativo como o Google Authenticator para confirmar sua identidade sempre que fizer login.
            </p>

            {/* === Download Google Authenticator (loja por SO) === */}
            <div className="mb-10">
              <p className="text-[11px] font-semibold uppercase tracking-[0.12em] text-gray-400 dark:text-gray-500 mb-3">
                Ainda não tem o app? Baixe na sua loja:
              </p>
              <div className="flex flex-wrap gap-3">
                {/* App Store - iOS */}
                <a
                  href="https://apps.apple.com/app/google-authenticator/id388497605"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Baixar Google Authenticator na App Store (iOS)"
                  className="inline-flex items-center gap-3 px-5 py-2.5 rounded-xl bg-black text-white hover:bg-gray-800 transition-all shadow-sm hover:shadow-md hover:-translate-y-0.5"
                >
                  <svg className="h-7 w-7" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                    <path d="M17.05 20.28c-.98.95-2.05.86-3.08.42-1.09-.45-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.42C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"/>
                  </svg>
                  <span className="flex flex-col items-start leading-tight">
                    <span className="text-[10px] uppercase tracking-wider opacity-80">Baixar na</span>
                    <span className="text-base font-semibold">App Store</span>
                  </span>
                </a>

                {/* Google Play - Android */}
                <a
                  href="https://play.google.com/store/apps/details?id=com.google.android.apps.authenticator2"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Baixar Google Authenticator no Google Play (Android)"
                  className="inline-flex items-center gap-3 px-5 py-2.5 rounded-xl bg-black text-white hover:bg-gray-800 transition-all shadow-sm hover:shadow-md hover:-translate-y-0.5"
                >
                  <svg className="h-7 w-7" viewBox="0 0 512 512" aria-hidden="true">
                    <path d="M325.3 234.3L104.6 13l280.8 161.2-60.1 60.1zM47 0C34 6.8 25.3 19.2 25.3 35.3v441.3c0 16.1 8.7 28.5 21.7 35.3l256.6-256L47 0zm425.2 225.6l-58.9-34.1-65.7 64.5 65.7 64.5 60.1-34.1c18-14.3 18-46.5-1.2-60.8zM104.6 499l280.8-161.2-60.1-60.1L104.6 499z" fill="#34A853"/>
                    <path d="M47 0l256.6 256L47 512c-13-6.8-21.7-19.2-21.7-35.3V35.3C25.3 19.2 34 6.8 47 0z" fill="#4285F4"/>
                    <path d="M325.3 277.7L47 512l281.7-160.6 56.7-32.7-60.1-60.1z" fill="#EA4335"/>
                    <path d="M325.3 234.3L47 0l281.7 160.6 56.7 32.7-60.1 60.1z" fill="#FBBC04"/>
                  </svg>
                  <span className="flex flex-col items-start leading-tight">
                    <span className="text-[10px] uppercase tracking-wider opacity-80">Baixar no</span>
                    <span className="text-base font-semibold">Google Play</span>
                  </span>
                </a>
              </div>
            </div>

            {/* Error */}
            {error && (
              <div className="mb-6 p-3 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-600 dark:text-red-400 text-sm text-center">
                {error}
              </div>
            )}

            {isLoading ? (
              <div className="flex flex-col items-center justify-center py-16 gap-3">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
                <p className="text-sm text-gray-500 dark:text-gray-400">Gerando QR code...</p>
              </div>
            ) : (
              <div className="flex flex-col md:flex-row gap-12">
                {/* Left column: QR Code */}
                <div className="flex flex-col items-center flex-shrink-0">
                  <p className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-4 text-center md:text-left self-stretch">
                    Escaneie o QR code no Google Authenticator:
                  </p>
                  {qrCode && (
                    <div className="bg-white rounded-xl p-4 border-2 border-gray-200 dark:border-gray-600 inline-block">
                      <img
                        src={qrCode}
                        alt="QR Code TOTP"
                        className="block"
                        style={{ width: '192px', height: '192px', imageRendering: 'pixelated' }}
                      />
                    </div>
                  )}
                  {secret && (
                    <p className="mt-4 text-[11px] text-gray-400 dark:text-gray-500 font-mono tracking-widest text-center break-all max-w-[230px] select-all leading-relaxed">
                      {secret}
                    </p>
                  )}
                </div>

                {/* Right column: Code Input */}
                <div className="flex-1 flex flex-col justify-center">
                  <p className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-5">
                    Apos escanear, digite o codigo de 6 digitos exibido no app:
                  </p>

                  <div className="flex gap-2 mb-8" onPaste={handlePaste}>
                    {code.map((digit, i) => (
                      <input
                        key={i}
                        ref={(el) => { inputRefs.current[i] = el }}
                        type="text"
                        inputMode="numeric"
                        maxLength={1}
                        value={digit}
                        onChange={(e) => handleInput(i, e.target.value)}
                        onKeyDown={(e) => handleKeyDown(i, e)}
                        className="w-11 h-12 text-center text-xl font-bold border-2 rounded-lg bg-gray-50 dark:bg-gray-800 text-gray-900 dark:text-white border-gray-300 dark:border-gray-600 focus:border-blue-500 dark:focus:border-blue-400 focus:ring-2 focus:ring-blue-500/20 focus:bg-white outline-none transition-all"
                        disabled={isVerifying}
                      />
                    ))}
                  </div>

                  <div className="flex items-center gap-4">
                    <Button
                      onClick={() => handleVerify()}
                      disabled={code.join('').length < 6 || isVerifying}
                      isLoading={isVerifying}
                      className="px-6 h-10"
                    >
                      <ShieldCheck className="h-4 w-4 mr-2" />
                      Verificar e Ativar
                    </Button>
                    <button
                      type="button"
                      onClick={handleCancel}
                      className="text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 transition-colors font-medium"
                    >
                      Cancelar
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <p className="text-center text-xs text-gray-400 dark:text-gray-600 mt-6">
          &copy; {new Date().getFullYear()} NAI IT. Todos os direitos reservados.
        </p>
      </div>
    </div>
  )
}
